import { z } from 'zod';
import { AppPlatformSchema } from './appConfig';
import {
  CHECKLIST_GRACE_MINUTES_MAX,
  CHECKLIST_ID_PATTERN,
  ChecklistScheduleSchema,
  RUN_ID_PATTERN,
} from './checklistSchedule';
import { APP_LOCALE_FAMILIES, ResponsibleIdSchema, TenantIdSchema, UserIdSchema } from './schemas';

/**
 * Documentos do módulo Checklists operacionais (`MOD_CHECKLISTS`): o checklist
 * do estabelecimento, a execução, a ação corretiva, as faixas do
 * estabelecimento, o agregado do dia e o espelho da biblioteca EtiquetaChef.
 *
 * Origem: #336 — 2026-10-19 22:53 +0200 (F1, contrato do módulo; §5 do corpo).
 *
 * Por quê: o módulo grava pelo CLIENTE, offline (`enqueueOfflineFirst`), ao
 * contrário dos outros módulos que gravam por callable. Então este schema é o
 * contrato que o trigger (`onChecklistRunWritten`, F2) usa para validar o que
 * chegou, e que os três clientes usam para ler. A autoridade sobre NC,
 * derivados e respostas inválidas é o trigger (princípio da #323): o cliente
 * calcula para mostrar, o servidor recalcula e grava.
 *
 * Contrato (caminhos, todos sob o tenant):
 *  - `tenants/{tid}/checklists/{checklistId}` — `ChecklistSchema`;
 *  - `tenants/{tid}/checklistRuns/{runId}` — `ChecklistRunSchema` (id em
 *    `checklistSchedule.ts`: `runDocId`/`onDemandRunId`);
 *  - `tenants/{tid}/correctiveActions/{actionId}` — `CorrectiveActionSchema`;
 *  - `tenants/{tid}/checklistConfig/ranges` — `ChecklistRangesConfigSchema`;
 *  - `tenants/{tid}/checklist_daily_ops/{yyyy-MM-dd}` — `ChecklistDailyOpsSchema`;
 *  - `config/checklistTemplates` e `config/checklistTemplates/items/{catalogKey}`
 *    — `ChecklistLibrarySchema` e `ChecklistTemplateSchema`, publicados por
 *    `apps/functions/scripts/sync-checklist-catalog.ts`.
 *
 * Convenção de tempo: `*AtMillis` é o relógio confiável do APARELHO
 * (`TrustedClock` no Android) — a hora exibida, de quando a pessoa mediu;
 * `*AtServer` é epoch-ms carimbado pelo TRIGGER com a hora do evento — a prova.
 * O `*AtServer` é opcional/nulo na janela entre a escrita do cliente e o
 * trigger (mesma janela tolerada de `ProductSchema.createdAt`). `ttlAt` é
 * `Timestamp` do Firestore (a política de TTL ignora número), por isso
 * `z.unknown()`, como `LabelSchema.ttlAt`.
 *
 * Usado por: (F1) `checklistCatalog.ts` (os modelos passam pelo
 * `ChecklistTemplateSchema`), o snapshot de contrato do iOS
 * (`contract/targets.ts` → `npm run contract`) e o espelho Kotlin dos enums
 * (`apps/android/.../domain/checklist/ChecklistEnums.kt`, conferido contra
 * `enums.json`). Previstos: triggers e rules (F2), telas Android (F3–F5b),
 * iOS (F6) e web (F7).
 *
 * Arquitetura: tenant scope em todo caminho; `tenantId` no doc é igual ao do
 * caminho (rule, F2). Status derivado NÃO é gravado ("com NC" = `COMPLETED` +
 * `hasNc`; pendente/atrasada são calculadas). `answers` é mapa por `itemId`,
 * nunca array nem subcoleção: dois aparelhos se fundem por campo (§5.2).
 *
 * Falhas conhecidas: nenhuma ainda. Parentes: #30/#43/#392 (enum do domínio
 * tipado direto no DTO derrubava o app) — os espelhos devem ler estes enums
 * com tolerância (`enumSafe` no Android), nunca `toObject` cru.
 *
 * Dicas: mexeu aqui → `npm run contract && npm run contract:swift` no mesmo
 * commit (gate `contract:check`), e o `ChecklistEnumsContractTest` do Android
 * acusa enum que divergiu. Os tetos (`CHECKLIST_ITEMS_MAX` etc.) também
 * limitam o tamanho do doc da execução, que carrega `itemsSnapshot` inteiro.
 *
 * Issues: #336 (épico, decisões 1–21), #323 (otimista + trigger), #984
 * (`alreadyApplied`), #989 (`markModuleRefused`).
 *
 * Histórico:
 *  - 2026-10-19 22:53 +0200 — (#336) F1: enums, ids e schemas do módulo.
 *  - 2026-10-19 23:30 +0200 — (#336) auditoria da F1: id de item sem `__`;
 *    faixa sem lados só no sanitizante; teto do texto do modelo igual ao da
 *    cópia; lacuna `MISSED → IN_PROGRESS` registrada para a F2.
 *  - 2026-10-19 23:40 +0200 — (#336) decisão 22: cabeçalho offline aceito
 *    sobre `MISSED` sem mudar o status (rule da F2).
 *  - 2026-10-20 00:05 +0200 — (#336) auditoria da F2: o cabeçalho é gravado
 *    SEM `status` (decisão 22 reescrita — a rule não altera a escrita); o
 *    segundo aparelho adota o cabeçalho do primeiro (decisão 23); ids com
 *    regex; `graceMinutes` congelado no cabeçalho; caminhos de foto da ação
 *    separados (`nc`/`resolution`).
 *  - 2026-10-20 00:40 +0200 — (#336) `participantUids` na execução e na ação
 *    (só o trigger grava), chave do export LGPD (achado 9 da auditoria da F2).
 */

// ------------------------------------------------------------------ ids

type Brand<K, T> = K & { readonly __brand: T };
export type ChecklistId = Brand<string, 'ChecklistId'>;
export type ChecklistRunId = Brand<string, 'ChecklistRunId'>;
export type CorrectiveActionId = Brand<string, 'CorrectiveActionId'>;

/**
 * Id da ação corretiva: `<runId>__<itemId>` quando nasce no trigger (uma por
 * item NC, idempotente na reentrega), ou alfanumérico até 64 (auto-id do
 * Firestore) quando é avulsa, criada por OWNER/MANAGER. Copiado à mão para
 * `firestore.rules` e `storage.rules` (RE2, casamento inteiro), como o
 * `RUN_ID_PATTERN`.
 */
export const CORRECTIVE_ACTION_ID_PATTERN = new RegExp(
  `^(?:${RUN_ID_PATTERN.source.slice(1, -1)}__[a-z0-9]+(?:_[a-z0-9]+)*|[A-Za-z0-9]{1,64})$`,
);

// `.meta` pelo mesmo motivo dos ids de `schemas.ts`: sem ele o snapshot de
// contrato do iOS não representa o `.transform` do brand.
//
// Auditoria 2026-10-20 00:05 +0200 (#336, F2, achado 11): os três ids eram só
// `min(1)`. Um `checklistId` com `_` ou `-` passava aqui e depois quebrava o
// `runDocId` (que exige `CHECKLIST_ID_PATTERN`) — o checklist existia mas não
// podia ser executado. A rule do create força a mesma regex.
export const ChecklistIdSchema = z
  .string()
  .regex(CHECKLIST_ID_PATTERN)
  .transform((s) => s as ChecklistId)
  .meta({ type: 'string', minLength: 1 });
export const ChecklistRunIdSchema = z
  .string()
  .regex(RUN_ID_PATTERN)
  .transform((s) => s as ChecklistRunId)
  .meta({ type: 'string', minLength: 1 });
export const CorrectiveActionIdSchema = z
  .string()
  .regex(CORRECTIVE_ACTION_ID_PATTERN)
  .transform((s) => s as CorrectiveActionId)
  .meta({ type: 'string', minLength: 1 });

// ------------------------------------------------------------------ enums

/** Grupo do checklist — as 8 seções da biblioteca (#336 §3.1–§3.8). */
export const ChecklistGroup = {
  OPENING_CLOSING: 'OPENING_CLOSING',
  TEMPERATURE: 'TEMPERATURE',
  RECEIVING_STORAGE: 'RECEIVING_STORAGE',
  CLEANING: 'CLEANING',
  FOOD_HANDLING: 'FOOD_HANDLING',
  STAFF: 'STAFF',
  PESTS_WASTE_MAINTENANCE: 'PESTS_WASTE_MAINTENANCE',
  AUDIT: 'AUDIT',
} as const;
export type ChecklistGroup = (typeof ChecklistGroup)[keyof typeof ChecklistGroup];
export const ALL_CHECKLIST_GROUPS = Object.values(ChecklistGroup) as [ChecklistGroup, ...ChecklistGroup[]];
export const ChecklistGroupSchema = z.enum(ALL_CHECKLIST_GROUPS);

/**
 * Ícone do checklist. Conjunto fechado: cada cliente mapeia para o símbolo da
 * plataforma (Material no Android/web, SF Symbol no iOS), e o editor oferece
 * só estes.
 */
export const ChecklistIcon = {
  KITCHEN: 'KITCHEN',
  SHIFT: 'SHIFT',
  CHEF: 'CHEF',
  THERMOMETER: 'THERMOMETER',
  SNOWFLAKE: 'SNOWFLAKE',
  TRUCK: 'TRUCK',
  BOX: 'BOX',
  CALENDAR: 'CALENDAR',
  LABEL: 'LABEL',
  INVENTORY: 'INVENTORY',
  SPRAY: 'SPRAY',
  BROOM: 'BROOM',
  OIL: 'OIL',
  ALLERGEN: 'ALLERGEN',
  HANDS: 'HANDS',
  UNIFORM: 'UNIFORM',
  BUG: 'BUG',
  TRASH: 'TRASH',
  WRENCH: 'WRENCH',
  CLIPBOARD: 'CLIPBOARD',
  WARNING: 'WARNING',
} as const;
export type ChecklistIcon = (typeof ChecklistIcon)[keyof typeof ChecklistIcon];
export const ALL_CHECKLIST_ICONS = Object.values(ChecklistIcon) as [ChecklistIcon, ...ChecklistIcon[]];
export const ChecklistIconSchema = z.enum(ALL_CHECKLIST_ICONS);

/** Tipo de item (#336 decisão 3). */
export const ChecklistItemKind = {
  /** OK / Não conforme / N.A. */
  CONFORMITY: 'CONFORMITY',
  /** Número contra faixa; fora da faixa = NC automática. */
  MEASUREMENT: 'MEASUREMENT',
  /** Escolha única; cada opção diz se é conforme. */
  SELECT_ONE: 'SELECT_ONE',
  /** Texto livre; nunca vira NC. */
  TEXT: 'TEXT',
} as const;
export type ChecklistItemKind = (typeof ChecklistItemKind)[keyof typeof ChecklistItemKind];
export const ALL_CHECKLIST_ITEM_KINDS = Object.values(ChecklistItemKind) as [
  ChecklistItemKind,
  ...ChecklistItemKind[],
];
export const ChecklistItemKindSchema = z.enum(ALL_CHECKLIST_ITEM_KINDS);

/**
 * Evidência exigida pelo item, MESMO quando a resposta é conforme. A NC exige
 * observação + foto sempre (decisão 6), independentemente desta política.
 */
export const ChecklistEvidence = {
  NONE: 'NONE',
  OPTIONAL: 'OPTIONAL',
  PHOTO_REQUIRED: 'PHOTO_REQUIRED',
  COMMENT_REQUIRED: 'COMMENT_REQUIRED',
  PHOTO_OR_COMMENT_REQUIRED: 'PHOTO_OR_COMMENT_REQUIRED',
} as const;
export type ChecklistEvidence = (typeof ChecklistEvidence)[keyof typeof ChecklistEvidence];
export const ALL_CHECKLIST_EVIDENCES = Object.values(ChecklistEvidence) as [
  ChecklistEvidence,
  ...ChecklistEvidence[],
];
export const ChecklistEvidenceSchema = z.enum(ALL_CHECKLIST_EVIDENCES);

/**
 * Estado da execução (§5.5). `COMPLETED` e `DISMISSED` são terminais;
 * `MISSED` só o servidor grava, e a única saída dele é `COMPLETED` (conclusão
 * offline que chegou depois). Fechado: status desconhecido não pode virar outro.
 */
export const ChecklistRunStatus = {
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  MISSED: 'MISSED',
  DISMISSED: 'DISMISSED',
} as const;
export type ChecklistRunStatus = (typeof ChecklistRunStatus)[keyof typeof ChecklistRunStatus];
export const ALL_CHECKLIST_RUN_STATUSES = Object.values(ChecklistRunStatus) as [
  ChecklistRunStatus,
  ...ChecklistRunStatus[],
];
export const ChecklistRunStatusSchema = z.enum(ALL_CHECKLIST_RUN_STATUSES);

/** Resposta de um item. `PENDING` = ainda não respondido (ou resposta apagada). */
export const ChecklistAnswerStatus = {
  OK: 'OK',
  NC: 'NC',
  NA: 'NA',
  PENDING: 'PENDING',
} as const;
export type ChecklistAnswerStatus = (typeof ChecklistAnswerStatus)[keyof typeof ChecklistAnswerStatus];
export const ALL_CHECKLIST_ANSWER_STATUSES = Object.values(ChecklistAnswerStatus) as [
  ChecklistAnswerStatus,
  ...ChecklistAnswerStatus[],
];
export const ChecklistAnswerStatusSchema = z.enum(ALL_CHECKLIST_ANSWER_STATUSES);

/**
 * De onde veio a resposta. No v1 só `MANUAL`; os outros estão reservados para
 * sensor de temperatura (#368), tag NFC e balança/sonda, fora do v1 (§13).
 */
export const ChecklistAnswerSource = {
  MANUAL: 'MANUAL',
  SENSOR: 'SENSOR',
  NFC: 'NFC',
  SCALE: 'SCALE',
} as const;
export type ChecklistAnswerSource = (typeof ChecklistAnswerSource)[keyof typeof ChecklistAnswerSource];
export const ALL_CHECKLIST_ANSWER_SOURCES = Object.values(ChecklistAnswerSource) as [
  ChecklistAnswerSource,
  ...ChecklistAnswerSource[],
];
export const ChecklistAnswerSourceSchema = z.enum(ALL_CHECKLIST_ANSWER_SOURCES);

/**
 * Estado da ação corretiva (§5.7): `OPEN → RESOLVED → VERIFIED`,
 * `RESOLVED → OPEN` (verificação recusada), `OPEN → CANCELLED`. "Vencida" é
 * calculada, nunca gravada.
 */
export const CorrectiveActionStatus = {
  OPEN: 'OPEN',
  RESOLVED: 'RESOLVED',
  VERIFIED: 'VERIFIED',
  CANCELLED: 'CANCELLED',
} as const;
export type CorrectiveActionStatus = (typeof CorrectiveActionStatus)[keyof typeof CorrectiveActionStatus];
export const ALL_CORRECTIVE_ACTION_STATUSES = Object.values(CorrectiveActionStatus) as [
  CorrectiveActionStatus,
  ...CorrectiveActionStatus[],
];
export const CorrectiveActionStatusSchema = z.enum(ALL_CORRECTIVE_ACTION_STATUSES);

/** Transições válidas da ação corretiva — a rule (F2) e as telas leem daqui. */
export const CORRECTIVE_ACTION_TRANSITIONS: Readonly<Record<CorrectiveActionStatus, readonly CorrectiveActionStatus[]>> = {
  OPEN: [CorrectiveActionStatus.RESOLVED, CorrectiveActionStatus.CANCELLED],
  RESOLVED: [CorrectiveActionStatus.VERIFIED, CorrectiveActionStatus.OPEN],
  VERIFIED: [],
  CANCELLED: [],
};

/**
 * Transições válidas da execução feitas pelo CLIENTE (§5.5, §8). O que não
 * está aqui só o servidor faz: "sem doc → MISSED" e `IN_PROGRESS → MISSED` são
 * do cron. `MISSED → COMPLETED` está no mapa de propósito: é a conclusão
 * offline que chegou depois do cron. A criação do doc não é transição e fica
 * fora do mapa: o cliente cria SEM `status` ("Iniciar", ver abaixo) ou direto
 * em `DISMISSED` (OWNER/MANAGER dispensa uma ocorrência que ninguém começou).
 * A chave `IN_PROGRESS` vale também para o doc sem `status` (em andamento).
 *
 * Cabeçalho sem `status` (decisão 22 do owner, 2026-10-19 23:40 +0200,
 * reescrita em 2026-10-20 00:00 +0200 pela auditoria da F2, #336): o aparelho
 * que deu "Iniciar" offline enfileira o CABEÇALHO antes das respostas e da
 * conclusão, e o cron pode ter gravado `MISSED` nesse meio-tempo. A versão
 * anterior mandava a rule aceitar o cabeçalho `IN_PROGRESS` "sem mudar o
 * status" — inexequível: a rule só aceita ou recusa o doc já mesclado. Por
 * isso o cabeçalho NÃO leva `status` (padrão do ledger de etiquetas, #323,
 * `LabelBaixaPatch.status = null`): sobre `MISSED`, o status continua `MISSED`;
 * em doc novo, fica ausente e o trigger preenche `IN_PROGRESS`.
 * `MISSED → IN_PROGRESS` continua fora deste mapa.
 *
 * Segundo aparelho (decisão 23, 2026-10-20 00:00 +0200): o cabeçalho é
 * imutável e vale o do primeiro; o "Iniciar" de outro aparelho é recusado e o
 * cliente trata a recusa como "já iniciada" (§5.6), nunca `markModuleRefused()`.
 */
export const CHECKLIST_RUN_CLIENT_TRANSITIONS: Readonly<Record<ChecklistRunStatus, readonly ChecklistRunStatus[]>> = {
  IN_PROGRESS: [ChecklistRunStatus.COMPLETED, ChecklistRunStatus.DISMISSED],
  MISSED: [ChecklistRunStatus.COMPLETED],
  COMPLETED: [],
  DISMISSED: [],
};

/** Gravidade da ação: crítica = nasceu de NC em item `critical` (avisa na hora). */
export const CorrectiveActionSeverity = {
  CRITICAL: 'CRITICAL',
  NORMAL: 'NORMAL',
} as const;
export type CorrectiveActionSeverity = (typeof CorrectiveActionSeverity)[keyof typeof CorrectiveActionSeverity];
export const ALL_CORRECTIVE_ACTION_SEVERITIES = Object.values(CorrectiveActionSeverity) as [
  CorrectiveActionSeverity,
  ...CorrectiveActionSeverity[],
];
export const CorrectiveActionSeveritySchema = z.enum(ALL_CORRECTIVE_ACTION_SEVERITIES);

/** As 13 faixas nomeadas do §3.9. Valores de fábrica em `checklistCatalog.ts`. */
export const ChecklistRangeKey = {
  COLD_STORAGE: 'COLD_STORAGE',
  FROZEN_STORAGE: 'FROZEN_STORAGE',
  HOT_HOLDING: 'HOT_HOLDING',
  COLD_DISPLAY: 'COLD_DISPLAY',
  COOKING_CORE: 'COOKING_CORE',
  COOLING_2H: 'COOLING_2H',
  RECEIVING_CHILLED: 'RECEIVING_CHILLED',
  RECEIVING_MEAT: 'RECEIVING_MEAT',
  RECEIVING_FISH: 'RECEIVING_FISH',
  RECEIVING_FROZEN: 'RECEIVING_FROZEN',
  FRYING_TEMP: 'FRYING_TEMP',
  FRYING_POLAR: 'FRYING_POLAR',
  SANITIZER: 'SANITIZER',
} as const;
export type ChecklistRangeKey = (typeof ChecklistRangeKey)[keyof typeof ChecklistRangeKey];
export const ALL_CHECKLIST_RANGE_KEYS = Object.values(ChecklistRangeKey) as [
  ChecklistRangeKey,
  ...ChecklistRangeKey[],
];
export const ChecklistRangeKeySchema = z.enum(ALL_CHECKLIST_RANGE_KEYS);

/** Origem do valor de uma faixa do estabelecimento: o de fábrica ou o editado. */
export const ChecklistRangeSource = {
  FACTORY: 'FACTORY',
  CUSTOM: 'CUSTOM',
} as const;
export type ChecklistRangeSource = (typeof ChecklistRangeSource)[keyof typeof ChecklistRangeSource];
export const ALL_CHECKLIST_RANGE_SOURCES = Object.values(ChecklistRangeSource) as [
  ChecklistRangeSource,
  ...ChecklistRangeSource[],
];
export const ChecklistRangeSourceSchema = z.enum(ALL_CHECKLIST_RANGE_SOURCES);

/** Unidade de medição. O sanitizante mede em ppm (cloro) ou % conforme o rótulo. */
export const ChecklistUnit = {
  CELSIUS: 'CELSIUS',
  PERCENT: 'PERCENT',
  PPM: 'PPM',
} as const;
export type ChecklistUnit = (typeof ChecklistUnit)[keyof typeof ChecklistUnit];
export const ALL_CHECKLIST_UNITS = Object.values(ChecklistUnit) as [ChecklistUnit, ...ChecklistUnit[]];
export const ChecklistUnitSchema = z.enum(ALL_CHECKLIST_UNITS);

/**
 * Limite físico de cada unidade (§6.3): fora disso é erro de digitação, não
 * medição. −60 °C cobre ultracongelador; 300 °C cobre forno e fritura.
 */
export const CHECKLIST_UNIT_LIMITS: Readonly<Record<ChecklistUnit, { readonly min: number; readonly max: number }>> = {
  CELSIUS: { min: -60, max: 300 },
  PERCENT: { min: 0, max: 100 },
  PPM: { min: 0, max: 10000 },
};

// ------------------------------------------------------------------ tetos

/** Itens por checklist (§5.1). Mantém a execução, que copia os itens, longe de 1 MiB. */
export const CHECKLIST_ITEMS_MAX = 60;
export const CHECKLIST_NAME_MAX = 80;
export const CHECKLIST_ITEM_LABEL_MAX = 200;
export const CHECKLIST_TEXT_MAX = 2000;
export const CHECKLIST_COMMENT_MAX = 1000;
export const CHECKLIST_OPTIONS_MIN = 2;
export const CHECKLIST_OPTIONS_MAX = 8;
export const CHECKLIST_OPTION_LABEL_MAX = 80;
/** Motivo da dispensa: 3–200 caracteres (§5.5). */
export const CHECKLIST_DISMISS_REASON_MIN = 3;
export const CHECKLIST_DISMISS_REASON_MAX = 200;
export const CHECKLIST_NOTE_MAX = 500;
/** Adendos por execução; append-only (§5.6). */
export const CHECKLIST_ADDENDA_MAX = 50;
/**
 * Teto de `participantUids`: 60 respostas + quem iniciou, concluiu, dispensou
 * e verificou + 50 adendos dá no máximo 114 pessoas distintas; 200 deixa folga
 * sem deixar o array crescer sem limite.
 */
export const CHECKLIST_PARTICIPANTS_MAX = 200;
/** Casas decimais de uma medição. */
export const CHECKLIST_DECIMALS_MAX = 2;

/**
 * Id local do item: slug `[a-z0-9_]`. Vira chave do mapa `answers` e parte do
 * caminho da foto e do id da ação (`<runId>__<itemId>`), por isso sem ponto,
 * barra ou maiúscula.
 *
 * Auditoria 2026-10-19 23:30 +0200 (#336, F1): aceitava `__` e `_` nas pontas
 * (`a__b`, `_x`), e o `__` é o separador do id da ação — `<runId>__a__b` não
 * se separa de volta. Mesma regra da chave de turno (`CHECKLIST_SHIFT_KEY_PATTERN`).
 */
export const CHECKLIST_ITEM_ID_PATTERN = /^(?=.{1,40}$)[a-z0-9]+(?:_[a-z0-9]+)*$/;
const ItemIdSchema = z.string().regex(CHECKLIST_ITEM_ID_PATTERN);

/** Chave do modelo na biblioteca (`KITCHEN_OPENING`). Lista viva em `checklistCatalog.ts`. */
export const CHECKLIST_CATALOG_KEY_PATTERN = /^[A-Z][A-Z0-9_]{1,47}$/;
export const ChecklistCatalogKeySchema = z.string().regex(CHECKLIST_CATALOG_KEY_PATTERN);

const MillisSchema = z.number().int().nonnegative();
/** Carimbo do trigger: ausente/nulo até o trigger rodar. */
const ServerMillisSchema = MillisSchema.nullable().optional();

// ------------------------------------------------------------------ faixas

/** `min < max` quando há os dois; os dois dentro do limite físico da unidade. */
function refineRange(
  r: { min: number | null; max: number | null; unit: ChecklistUnit },
  ctx: z.RefinementCtx,
): void {
  const limits = CHECKLIST_UNIT_LIMITS[r.unit];
  for (const side of ['min', 'max'] as const) {
    const v = r[side];
    if (v !== null && (v < limits.min || v > limits.max)) {
      ctx.addIssue({ code: 'custom', path: [side], message: 'fora do limite físico da unidade' });
    }
  }
  if (r.min !== null && r.max !== null && r.min >= r.max) {
    ctx.addIssue({ code: 'custom', path: ['max'], message: 'min deve ser menor que max' });
  }
}

/**
 * Só o sanitizante pode ficar sem os dois lados: a norma manda seguir o rótulo
 * (§3.9) e, enquanto a faixa do rótulo não é configurada, a medição só registra
 * o valor. Qualquer outra faixa sem lados desligaria a NC automática em silêncio.
 *
 * Auditoria 2026-10-19 23:30 +0200 (#336, F1): o schema aceitava os dois lados
 * nulos em QUALQUER faixa nomeada (item, faixas do estabelecimento e de
 * fábrica); a docstring e o §3.9 diziam "só no sanitizante".
 */
function refineBothSidesOnlySanitizer(
  key: ChecklistRangeKey | null,
  r: { min: number | null; max: number | null },
  ctx: z.RefinementCtx,
  path: (string | number)[] = [],
): void {
  if (r.min === null && r.max === null && key !== ChecklistRangeKey.SANITIZER) {
    ctx.addIssue({
      code: 'custom',
      path: [...path, 'min'],
      message: key === null ? 'faixa própria exige ao menos um lado' : 'só o sanitizante fica sem os dois lados',
    });
  }
}

/**
 * Valor de uma faixa do estabelecimento. Um lado pode ser nulo (≥ 75 °C);
 * os dois nulos só no sanitizante sem rótulo configurado — aí a medição só
 * registra o valor, sem NC automática (§3.9).
 */
export const ChecklistRangeValueSchema = z
  .object({
    min: z.number().nullable(),
    max: z.number().nullable(),
    unit: ChecklistUnitSchema,
    source: ChecklistRangeSourceSchema,
  })
  .superRefine(refineRange);
export type ChecklistRangeValue = z.infer<typeof ChecklistRangeValueSchema>;

/**
 * Faixas do estabelecimento — `tenants/{tid}/checklistConfig/ranges` (§6.3).
 *
 * Dados — exemplo:
 *   { "items": { "COLD_STORAGE": { "min": 0, "max": 4, "unit": "CELSIUS", "source": "FACTORY" },
 *                "COOKING_CORE": { "min": 74, "max": null, "unit": "CELSIUS", "source": "CUSTOM" } },
 *     "confirmedAtMillis": 1760900000000, "confirmedByUid": "uid-1" }
 *
 * Escreve: OWNER/MANAGER pela sheet "Faixas do estabelecimento". Lê: todo
 * membro (editor e execução). Chave ausente em `items` = vale a de fábrica.
 * Doc ausente = faixas ainda não confirmadas (abre a sheet no 1º uso da
 * biblioteca).
 */
export const ChecklistRangesConfigSchema = z
  .object({
    items: z.partialRecord(ChecklistRangeKeySchema, ChecklistRangeValueSchema),
    confirmedAtMillis: MillisSchema.nullable(),
    confirmedAtServer: ServerMillisSchema,
    confirmedByUid: UserIdSchema.nullable(),
  })
  .superRefine((cfg, ctx) => {
    for (const key of ALL_CHECKLIST_RANGE_KEYS) {
      const r = cfg.items[key];
      if (r) refineBothSidesOnlySanitizer(key, r, ctx, ['items', key]);
    }
  });
export type ChecklistRangesConfig = z.infer<typeof ChecklistRangesConfigSchema>;

// ------------------------------------------------------------------ itens

const ItemBase = {
  id: ItemIdSchema,
  order: z.number().int().nonnegative(),
  label: z.string().min(1).max(CHECKLIST_ITEM_LABEL_MAX),
  required: z.boolean(),
  /** NC neste item notifica OWNER/MANAGER na hora (decisão 6) e gera ação `CRITICAL`. */
  critical: z.boolean(),
  evidence: ChecklistEvidenceSchema,
};

export const ChecklistOptionSchema = z.object({
  id: ItemIdSchema,
  label: z.string().min(1).max(CHECKLIST_OPTION_LABEL_MAX),
  conforming: z.boolean(),
});
export type ChecklistOption = z.infer<typeof ChecklistOptionSchema>;

const MeasurementItem = z
  .object({
    ...ItemBase,
    kind: z.literal(ChecklistItemKind.MEASUREMENT),
    unit: ChecklistUnitSchema,
    min: z.number().nullable(),
    max: z.number().nullable(),
    decimals: z.number().int().min(0).max(CHECKLIST_DECIMALS_MAX),
    /** Faixa nomeada de onde o valor veio; `null` = faixa própria do item. */
    rangeKey: ChecklistRangeKeySchema.nullable(),
  })
  .superRefine((item, ctx) => {
    refineRange(item, ctx);
    // Faixa própria sem lado nenhum não mede nada contra coisa nenhuma; com
    // `rangeKey`, só o sanitizante esperando a faixa do rótulo.
    refineBothSidesOnlySanitizer(item.rangeKey, item, ctx);
  });

const SelectOneItem = z
  .object({
    ...ItemBase,
    kind: z.literal(ChecklistItemKind.SELECT_ONE),
    options: z.array(ChecklistOptionSchema).min(CHECKLIST_OPTIONS_MIN).max(CHECKLIST_OPTIONS_MAX),
  })
  .superRefine((item, ctx) => {
    if (!item.options.some((o) => o.conforming)) {
      ctx.addIssue({ code: 'custom', path: ['options'], message: 'ao menos uma opção conforme' });
    }
    if (new Set(item.options.map((o) => o.id)).size !== item.options.length) {
      ctx.addIssue({ code: 'custom', path: ['options'], message: 'opção repetida' });
    }
  });

/**
 * Item do checklist — união discriminada por `kind` (§5.1). O mesmo shape vai
 * congelado no `itemsSnapshot` da execução, com a faixa daquele momento.
 */
export const ChecklistItemSchema = z.discriminatedUnion('kind', [
  z.object({ ...ItemBase, kind: z.literal(ChecklistItemKind.CONFORMITY) }),
  MeasurementItem,
  SelectOneItem,
  z.object({
    ...ItemBase,
    kind: z.literal(ChecklistItemKind.TEXT),
    maxLength: z.number().int().min(1).max(CHECKLIST_TEXT_MAX),
  }),
]);
export type ChecklistItem = z.infer<typeof ChecklistItemSchema>;

const ItemListSchema = z
  .array(ChecklistItemSchema)
  .min(1)
  .max(CHECKLIST_ITEMS_MAX)
  .refine((items) => new Set(items.map((i) => i.id)).size === items.length, { message: 'itemId repetido' });

// ------------------------------------------------------------------ checklist

/**
 * O checklist do estabelecimento — `tenants/{tid}/checklists/{checklistId}`
 * (§5.1). Nasce de um modelo da biblioteca (cópia independente,
 * `catalogKey` = procedência) ou em branco (`catalogKey: null`).
 *
 * Dados — exemplo:
 *   { "id": "AbCdEfGhIjKlMnOpQrSt", "tenantId": "tenant-1",
 *     "catalogKey": "FRIDGE_TEMPERATURE", "name": "Temperatura das geladeiras",
 *     "group": "TEMPERATURE", "icon": "THERMOMETER", "active": true, "order": 3,
 *     "schedule": { …ChecklistScheduleSchema… },
 *     "items": [{ "id": "fridge_1", "order": 0, "kind": "MEASUREMENT", "label": "Geladeira 1",
 *                 "required": true, "critical": true, "evidence": "NONE",
 *                 "unit": "CELSIUS", "min": 0, "max": 4, "decimals": 1, "rangeKey": "COLD_STORAGE" }],
 *     "version": 4, "requiresResponsible": true,
 *     "createdAt": 1760900000000, "updatedAt": 1760990000000,
 *     "createdByUid": "uid-1", "updatedByUid": "uid-2" }
 *
 * Sem delete: desativar com `active: false`. `version` sobe 1 a cada edição; a
 * rule (F2) recusa a segunda gravação concorrente que chega à mesma versão.
 * `createdAt`/`updatedAt` são carimbados pelo trigger (`onChecklistWritten`).
 */
export const ChecklistSchema = z.object({
  id: ChecklistIdSchema,
  tenantId: TenantIdSchema,
  catalogKey: ChecklistCatalogKeySchema.nullable(),
  name: z.string().min(1).max(CHECKLIST_NAME_MAX),
  group: ChecklistGroupSchema,
  icon: ChecklistIconSchema,
  active: z.boolean(),
  order: z.number().int().nonnegative(),
  schedule: ChecklistScheduleSchema,
  items: ItemListSchema,
  version: z.number().int().min(1),
  requiresResponsible: z.boolean(),
  createdAt: ServerMillisSchema,
  updatedAt: ServerMillisSchema,
  createdByUid: UserIdSchema.optional(),
  updatedByUid: UserIdSchema.optional(),
});
export type Checklist = z.infer<typeof ChecklistSchema>;

// ------------------------------------------------------------------ execução

/**
 * Resposta de um item — `answers.<itemId>` da execução. O cliente grava SÓ as
 * chaves alteradas (`answers.<k>`), nunca o mapa inteiro (§5.2).
 *
 * `comment` é a observação: obrigatória na NC (decisão 6) e na evidência
 * `COMMENT_REQUIRED`. `text` é a resposta do item `TEXT`. Os dois são campos
 * separados para o item de texto poder ter observação própria.
 */
export const ChecklistAnswerSchema = z.object({
  status: ChecklistAnswerStatusSchema,
  value: z.number().nullable(),
  optionId: ItemIdSchema.nullable(),
  text: z.string().max(CHECKLIST_TEXT_MAX).nullable(),
  comment: z.string().max(CHECKLIST_COMMENT_MAX).nullable(),
  /** `tenants/{tid}/checklistRuns/{runId}/{itemId}.jpg` no Storage (`checklistRunPhotoPath`). */
  photoPath: z.string().min(1).max(500).nullable(),
  /** Foto tirada e ainda no aparelho; quem baixa para `false` é o trigger de Storage (§6.4). */
  photoPending: z.boolean(),
  answeredAtMillis: MillisSchema,
  answeredAtServer: ServerMillisSchema,
  answeredByUid: UserIdSchema,
  answeredByName: z.string().max(120),
  source: ChecklistAnswerSourceSchema,
});
export type ChecklistAnswer = z.infer<typeof ChecklistAnswerSchema>;

/** Dispensa de uma ocorrência por OWNER/MANAGER (§5.5). */
export const ChecklistDismissalSchema = z.object({
  reason: z.string().min(CHECKLIST_DISMISS_REASON_MIN).max(CHECKLIST_DISMISS_REASON_MAX),
  byUid: UserIdSchema,
  atMillis: MillisSchema,
  atServer: ServerMillisSchema,
});

/** Visto do gerente na execução concluída (decisão 17). */
export const ChecklistVerificationSchema = z.object({
  byUid: UserIdSchema,
  atMillis: MillisSchema,
  atServer: ServerMillisSchema,
  note: z.string().max(CHECKLIST_NOTE_MAX).nullable(),
});

/** Adendo depois da conclusão; append-only (§5.6). */
export const ChecklistAddendumSchema = z.object({
  text: z.string().min(1).max(CHECKLIST_COMMENT_MAX),
  byUid: UserIdSchema,
  atMillis: MillisSchema,
  atServer: ServerMillisSchema,
});

/**
 * A execução — `tenants/{tid}/checklistRuns/{runId}` (§5.2). Coleção irmã de
 * `checklists`, não subcoleção: "o que vence hoje" é query simples no cliente.
 *
 * Dados — exemplo (resumido):
 *   { "id": "AbCdEfGhIjKlMnOpQrSt__2026-09-03__MORNING", "tenantId": "tenant-1",
 *     "checklistId": "AbCdEfGhIjKlMnOpQrSt", "checklistNameSnapshot": "Temperatura das geladeiras",
 *     "checklistVersion": 4, "itemsSnapshot": [ …ChecklistItem… ], "itemIds": ["fridge_1"],
 *     "periodKey": "2026-09-03__MORNING", "shiftKey": "MORNING",
 *     "periodStartMillis": 1756890000000, "periodEndMillis": 1756918799999, "dueAtMillis": 1756918800000,
 *     "graceMinutes": 60,
 *     "timeZoneAtStart": "America/Sao_Paulo", "startedAtMillis": 1756891000000, "startedByUid": "uid-3",
 *     "status": "COMPLETED", "platform": "android",
 *     "answers": { "fridge_1": { "status": "NC", "value": 7.5, "optionId": null, "text": null,
 *                  "comment": "Porta aberta", "photoPath": null, "photoPending": true,
 *                  "answeredAtMillis": 1756891200000, "answeredByUid": "uid-3",
 *                  "answeredByName": "Ana", "source": "MANUAL" } },
 *     "responsibleId": "resp-1", "responsibleName": "Ana Silva",
 *     "completedAtMillis": 1756891500000, "completedByUid": "uid-3",
 *     "dismissal": null, "verification": null, "addenda": [],
 *     "ncCount": 1, "hasNc": true, "answeredCount": 1, "itemCount": 1, "photoPendingCount": 1 }
 *
 * Grupos de campos:
 *  - cabeçalho (gravado uma vez, em "Iniciar", imutável pela rule): ids,
 *    snapshots, período, `graceMinutes`, `timeZoneAtStart`, `started*`. Vai
 *    SEM `status` (decisão 22). Sob demanda: `periodKey`/`shiftKey`/
 *    `periodStart/End`/`dueAt`/`graceMinutes` nulos.
 *  - estado: `status`, responsável, `completed*`, `dismissal`, `platform`.
 *  - pós-conclusão: `verification`, `addenda`.
 *  - só o trigger: `ncCount`, `hasNc`, `answeredCount`, `itemCount`,
 *    `photoPendingCount`, `invalidAnswers`, `periodDrift`, `completedLate`,
 *    `participantUids`, `*AtServer`, `ttlAt` — opcionais porque não existem
 *    antes do trigger.
 */
export const ChecklistRunSchema = z.object({
  id: ChecklistRunIdSchema,
  tenantId: TenantIdSchema,
  checklistId: ChecklistIdSchema,
  checklistNameSnapshot: z.string().min(1).max(CHECKLIST_NAME_MAX),
  checklistVersion: z.number().int().min(1),
  itemsSnapshot: ItemListSchema,
  itemIds: z.array(ItemIdSchema).min(1).max(CHECKLIST_ITEMS_MAX),
  periodKey: z.string().min(1).max(64).nullable(),
  shiftKey: z.string().min(1).max(24).nullable(),
  periodStartMillis: MillisSchema.nullable(),
  periodEndMillis: MillisSchema.nullable(),
  dueAtMillis: MillisSchema.nullable(),
  /**
   * Carência da agenda no momento do "Iniciar" (minutos). Congelada no
   * cabeçalho para o trigger calcular `completedLate` sem ler o checklist, que
   * pode já estar em outra `version` (auditoria da F2, #336, achado 13). Nula
   * no sob demanda, que não vence.
   */
  graceMinutes: z.number().int().min(0).max(CHECKLIST_GRACE_MINUTES_MAX).nullable(),
  timeZoneAtStart: z.string().min(1).max(64),
  startedAtMillis: MillisSchema,
  startedAtServer: ServerMillisSchema,
  /** `SYSTEM_ACTOR_UID` quando o doc nasceu no servidor (`MISSED` sem "Iniciar"). */
  startedByUid: UserIdSchema,
  /**
   * Ausente = em andamento: o cabeçalho do cliente não leva `status` (decisão
   * 22) e o trigger preenche `IN_PROGRESS`. Use `effectiveRunStatus` para ler.
   */
  status: ChecklistRunStatusSchema.optional(),
  platform: AppPlatformSchema.nullable(),
  answers: z.record(ItemIdSchema, ChecklistAnswerSchema),
  responsibleId: ResponsibleIdSchema.nullable(),
  responsibleName: z.string().max(120).nullable(),
  completedAtMillis: MillisSchema.nullable(),
  completedAtServer: ServerMillisSchema,
  completedByUid: UserIdSchema.nullable(),
  dismissal: ChecklistDismissalSchema.nullable(),
  verification: ChecklistVerificationSchema.nullable(),
  addenda: z.array(ChecklistAddendumSchema).max(CHECKLIST_ADDENDA_MAX),
  // Derivados — só o trigger grava (F2).
  ncCount: z.number().int().nonnegative().optional(),
  hasNc: z.boolean().optional(),
  answeredCount: z.number().int().nonnegative().optional(),
  itemCount: z.number().int().nonnegative().optional(),
  photoPendingCount: z.number().int().nonnegative().optional(),
  invalidAnswers: z.boolean().optional(),
  /**
   * Resultado da conferência do cabeçalho contra o checklist do servidor
   * (`VERIFIED | CORRECTED | REJECTED | UNVERIFIED`, `ChecklistRunHeaderCheck`). Só o trigger grava; ausente
   * = ainda não conferido. `CORRECTED` = o `itemsSnapshot`/prazo vindos do cliente divergiam e o
   * servidor os sobrescreveu (auditoria 2026-10-22 08:34 +0200, #336); a execução fica
   * `invalidAnswers`. É `string` (não enum do contrato) para o cliente nunca falhar ao ler um valor novo.
   */
  headerCheck: z.string().min(1).max(24).optional(),
  periodDrift: z.boolean().optional(),
  /** Conclusão chegou depois do prazo + carência (§5.5); `false` = no prazo, só sincronizou tarde. */
  completedLate: z.boolean().optional(),
  /**
   * Todo uid que aparece na execução (`startedByUid`, `completedByUid`,
   * `answers.*.answeredByUid`, `dismissal.byUid`, `verification.byUid`,
   * `addenda[].byUid`), ordenado, sem `SYSTEM_ACTOR_UID`. Só o trigger grava.
   * É a chave do export LGPD (`array-contains` em collection group): o mapa
   * `answers` não é consultável por valor.
   *
   * Auditoria 2026-10-20 00:05 +0200 (#336, F2, achado 9): buscar só por
   * `completedByUid` deixava de fora quem iniciou, respondeu, dispensou,
   * verificou ou anotou.
   */
  participantUids: z.array(UserIdSchema).max(CHECKLIST_PARTICIPANTS_MAX).optional(),
  /** `Timestamp` do Firestore (TTL de 180 dias, decisão 18); só o servidor lê. */
  ttlAt: z.unknown().nullable().optional(),
});
export type ChecklistRun = z.infer<typeof ChecklistRunSchema>;

/** Valores de `ChecklistRun.headerCheck` (só o trigger `onChecklistRunWritten` grava). */
export const ChecklistRunHeaderCheck = {
  VERIFIED: 'VERIFIED',
  CORRECTED: 'CORRECTED',
  /** O checklist de origem não existe: nada é derivado da execução (sem NC, ação nem aviso). */
  REJECTED: 'REJECTED',
  UNVERIFIED: 'UNVERIFIED',
} as const;
export type ChecklistRunHeaderCheck = (typeof ChecklistRunHeaderCheck)[keyof typeof ChecklistRunHeaderCheck];

/** Status da execução para leitura: ausente (cabeçalho do cliente, decisão 22) = `IN_PROGRESS`. */
// `| undefined` explícito: o `z.infer` de `status: ….optional()` devolve `status?: T | undefined`, e com
// `exactOptionalPropertyTypes` o tipo estreito recusava o próprio `ChecklistRun` (#336, R25, 2026-10-20 22:50 +0200).
export function effectiveRunStatus(run: { status?: ChecklistRunStatus | null | undefined }): ChecklistRunStatus {
  return run.status ?? ChecklistRunStatus.IN_PROGRESS;
}

// ------------------------------------------------------------------ fotos

/** Qual foto da ação: a da NC (avulsa) ou a da resolução. */
export const CorrectiveActionPhotoKind = {
  NC: 'nc',
  RESOLUTION: 'resolution',
} as const;
export type CorrectiveActionPhotoKind = (typeof CorrectiveActionPhotoKind)[keyof typeof CorrectiveActionPhotoKind];

/** Foto de uma resposta: `tenants/{tid}/checklistRuns/{runId}/{itemId}.jpg` (§5.8). */
export function checklistRunPhotoPath(tenantId: string, runId: string, itemId: string): string {
  return `tenants/${tenantId}/checklistRuns/${runId}/${itemId}.jpg`;
}

/**
 * Foto da ação: `tenants/{tid}/correctiveActions/{actionId}/{nc|resolution}.jpg`.
 * A ação que nasce no trigger NÃO tem foto `nc` própria: o `ncPhotoPath` dela
 * aponta para a foto da resposta na execução (sem cópia).
 *
 * Auditoria 2026-10-20 00:05 +0200 (#336, F2, achado 15): o corpo previa um
 * caminho só (`{actionId}.jpg`) para as duas fotos da ação.
 */
export function correctiveActionPhotoPath(tenantId: string, actionId: string, kind: CorrectiveActionPhotoKind): string {
  return `tenants/${tenantId}/correctiveActions/${actionId}/${kind}.jpg`;
}

/** Caminho de foto do módulo, decomposto — o que o trigger de Storage precisa para dar baixa. */
export type ChecklistPhotoRef =
  | { readonly kind: 'RUN'; readonly tenantId: string; readonly runId: string; readonly itemId: string }
  | {
      readonly kind: 'ACTION';
      readonly tenantId: string;
      readonly actionId: string;
      readonly photo: CorrectiveActionPhotoKind;
    };

const RUN_PHOTO_RE = /^tenants\/([^/]+)\/checklistRuns\/([^/]+)\/([^/]+)\.jpg$/;
const ACTION_PHOTO_RE = /^tenants\/([^/]+)\/correctiveActions\/([^/]+)\/(nc|resolution)\.jpg$/;

/**
 * Decompõe o caminho de uma foto do módulo, ou `null` se não é foto de
 * checklist (o trigger de Storage recebe TODO objeto do bucket). Confere os
 * ids pelas mesmas regex das rules: caminho fora do formato nunca vira escrita
 * no Firestore.
 */
export function parseChecklistPhotoPath(path: string): ChecklistPhotoRef | null {
  const run = RUN_PHOTO_RE.exec(path);
  if (run) {
    const [, tenantId, runId, itemId] = run as unknown as [string, string, string, string];
    if (!RUN_ID_PATTERN.test(runId) || !CHECKLIST_ITEM_ID_PATTERN.test(itemId)) return null;
    return { kind: 'RUN', tenantId, runId, itemId };
  }
  const action = ACTION_PHOTO_RE.exec(path);
  if (action) {
    const [, tenantId, actionId, photo] = action as unknown as [string, string, string, CorrectiveActionPhotoKind];
    if (!CORRECTIVE_ACTION_ID_PATTERN.test(actionId)) return null;
    return { kind: 'ACTION', tenantId, actionId, photo };
  }
  return null;
}

// ------------------------------------------------------------------ ação

/**
 * Ação corretiva — `tenants/{tid}/correctiveActions/{actionId}` (§5.7).
 *
 * Nasce no trigger, uma por item NC, quando a execução vai a `COMPLETED`
 * (id `<runId>__<itemId>`, idempotente na reentrega); ou avulsa, criada por
 * OWNER/MANAGER (`runId`/`checklistId`/`itemId` nulos). A máquina de estados
 * é `CORRECTIVE_ACTION_TRANSITIONS`, conferida pela rule (F2).
 *
 * Dados — exemplo:
 *   { "id": "<runId>__fridge_1", "tenantId": "tenant-1", "runId": "<runId>",
 *     "checklistId": "AbCdEfGhIjKlMnOpQrSt", "itemId": "fridge_1",
 *     "checklistNameSnapshot": "Temperatura das geladeiras", "itemLabelSnapshot": "Geladeira 1",
 *     "severity": "CRITICAL", "description": "Porta aberta", "ncPhotoPath": "tenants/…/fridge_1.jpg",
 *     "status": "OPEN", "assignedToUid": null, "dueAtMillis": null, … ,
 *     "createdByUid": "__system__", "createdAtServer": 1756891600000 }
 *
 * `ttlAt` só é gravado no estado terminal: ação aberta nunca expira.
 *
 * Fotos (`correctiveActionPhotoPath`): `ncPhotoPath` da ação do trigger aponta
 * para a foto da resposta na execução; a avulsa usa `{actionId}/nc.jpg`; a
 * resolução, `{actionId}/resolution.jpg`. O `onDelete` da execução não apaga
 * foto que uma ação não terminal ainda referencia (F2, achado 6).
 */
export const CorrectiveActionSchema = z.object({
  id: CorrectiveActionIdSchema,
  tenantId: TenantIdSchema,
  runId: ChecklistRunIdSchema.nullable(),
  checklistId: ChecklistIdSchema.nullable(),
  itemId: ItemIdSchema.nullable(),
  checklistNameSnapshot: z.string().max(CHECKLIST_NAME_MAX).nullable(),
  itemLabelSnapshot: z.string().max(CHECKLIST_ITEM_LABEL_MAX).nullable(),
  severity: CorrectiveActionSeveritySchema,
  description: z.string().min(1).max(CHECKLIST_COMMENT_MAX),
  ncPhotoPath: z.string().min(1).max(500).nullable(),
  /** Foto da NC ainda no aparelho de quem respondeu; o trigger de Storage baixa. */
  photoPending: z.boolean().optional(),
  status: CorrectiveActionStatusSchema,
  assignedToUid: UserIdSchema.nullable(),
  dueAtMillis: MillisSchema.nullable(),
  resolutionNote: z.string().max(CHECKLIST_COMMENT_MAX).nullable(),
  resolutionPhotoPath: z.string().min(1).max(500).nullable(),
  resolvedByUid: UserIdSchema.nullable(),
  resolvedAtMillis: MillisSchema.nullable(),
  resolvedAtServer: ServerMillisSchema,
  verifiedByUid: UserIdSchema.nullable(),
  verifiedAtMillis: MillisSchema.nullable(),
  verifiedAtServer: ServerMillisSchema,
  cancelReason: z.string().max(CHECKLIST_DISMISS_REASON_MAX).nullable(),
  /**
   * Quem cancelou / quem recusou a verificação e reabriu (gerente, `== auth.uid` pela rule).
   * Auditoria da F2 (2026-10-20, #336): sem autor, o activity log registrava `__system__`.
   */
  cancelledByUid: UserIdSchema.nullable().optional(),
  reopenedByUid: UserIdSchema.nullable().optional(),
  /** `SYSTEM_ACTOR_UID` quando nasceu no trigger. */
  createdByUid: UserIdSchema,
  createdAtServer: ServerMillisSchema,
  /**
   * Uids de quem criou, foi atribuído, resolveu ou verificou (sem
   * `SYSTEM_ACTOR_UID`), ordenados. Só o trigger grava; chave do export LGPD
   * (achado 9 da auditoria da F2, como em `ChecklistRunSchema`).
   */
  participantUids: z.array(UserIdSchema).max(CHECKLIST_PARTICIPANTS_MAX).optional(),
  ttlAt: z.unknown().nullable().optional(),
});
export type CorrectiveAction = z.infer<typeof CorrectiveActionSchema>;

// ------------------------------------------------------------------ agregado

const CountSchema = z.number().int().nonnegative().default(0);

/** Contadores de um checklist no dia (`byChecklist.<checklistId>`). */
export const ChecklistDailyCountsSchema = z.object({
  scheduled: CountSchema,
  completed: CountSchema,
  missed: CountSchema,
  dismissed: CountSchema,
  nc: CountSchema,
});

/**
 * Agregado do dia — `tenants/{tid}/checklist_daily_ops/{yyyy-MM-dd}` (§5.8).
 * Escrito só pelo servidor (F5), permanente (sem dado pessoal), lido por
 * OWNER/MANAGER na aba Checklists do Monitor e no card da Home. O dia é o do
 * fuso do tenant. NÃO mora no `daily_ops` das etiquetas (§13).
 */
export const ChecklistDailyOpsSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  scheduled: CountSchema,
  completed: CountSchema,
  completedOnTime: CountSchema,
  completedLate: CountSchema,
  missed: CountSchema,
  dismissed: CountSchema,
  onDemand: CountSchema,
  nc: CountSchema,
  ncCritical: CountSchema,
  verified: CountSchema,
  actionsOpened: CountSchema,
  actionsResolved: CountSchema,
  actionsVerified: CountSchema,
  byChecklist: z.record(z.string().min(1), ChecklistDailyCountsSchema).default({}),
  updatedAt: ServerMillisSchema,
});
export type ChecklistDailyOps = z.infer<typeof ChecklistDailyOpsSchema>;

// ------------------------------------------------------------------ biblioteca

/**
 * Texto nos 6 idiomas completos (`APP_LOCALE_FAMILIES`). A cópia para o
 * estabelecimento usa a família do idioma do app (`appLocaleFamily`), então
 * as regionais (pt-PT, es-AR…) herdam a língua-base (§3.10).
 */
type L10nShape = Record<(typeof APP_LOCALE_FAMILIES)[number], z.ZodString>;
function l10n(max: number): z.ZodObject<L10nShape> {
  return z.object(
    Object.fromEntries(APP_LOCALE_FAMILIES.map((locale) => [locale, z.string().min(1).max(max)])) as L10nShape,
  );
}
/** Teto da fonte legal de uma faixa de fábrica (texto da sheet, não vai para a cópia). */
export const CHECKLIST_RANGE_SOURCE_MAX = 300;
// Auditoria 2026-10-19 23:30 +0200 (#336, F1): o texto do modelo aceitava 300
// caracteres em tudo, mas a cópia exige nome ≤ `CHECKLIST_NAME_MAX` e rótulo ≤
// `CHECKLIST_ITEM_LABEL_MAX` — um modelo válido no espelho geraria uma cópia
// que o próprio schema recusa. Cada campo herda o teto de onde o texto vai parar.
export const ChecklistL10nSchema = l10n(CHECKLIST_RANGE_SOURCE_MAX);
export type ChecklistL10n = z.infer<typeof ChecklistL10nSchema>;
const NameL10nSchema = l10n(CHECKLIST_NAME_MAX);
const LabelL10nSchema = l10n(CHECKLIST_ITEM_LABEL_MAX);

const TemplateItemBase = {
  id: ItemIdSchema,
  label: LabelL10nSchema,
  required: z.boolean(),
  critical: z.boolean(),
  evidence: ChecklistEvidenceSchema,
};

/**
 * Item de modelo da biblioteca. A medição aponta SEMPRE para uma faixa
 * nomeada: o valor entra na cópia a partir das faixas do estabelecimento
 * (§6.2), nunca daqui. A ordem do item é a posição no array.
 */
export const ChecklistTemplateItemSchema = z.discriminatedUnion('kind', [
  z.object({ ...TemplateItemBase, kind: z.literal(ChecklistItemKind.CONFORMITY) }),
  z.object({
    ...TemplateItemBase,
    kind: z.literal(ChecklistItemKind.MEASUREMENT),
    rangeKey: ChecklistRangeKeySchema,
    decimals: z.number().int().min(0).max(CHECKLIST_DECIMALS_MAX),
  }),
  z.object({
    ...TemplateItemBase,
    kind: z.literal(ChecklistItemKind.TEXT),
    maxLength: z.number().int().min(1).max(CHECKLIST_TEXT_MAX),
  }),
]);
export type ChecklistTemplateItem = z.infer<typeof ChecklistTemplateItemSchema>;

/**
 * Modelo da biblioteca EtiquetaChef — `config/checklistTemplates/items/{catalogKey}`.
 * Fonte: `CHECKLIST_CATALOG` (`checklistCatalog.ts`); espelho publicado por
 * `apps/functions/scripts/sync-checklist-catalog.ts`. Lido pelos três
 * clientes (rule `read: isSignedIn()`, F2) — modelo corrigido entra sem release.
 */
export const ChecklistTemplateSchema = z
  .object({
    catalogKey: ChecklistCatalogKeySchema,
    group: ChecklistGroupSchema,
    icon: ChecklistIconSchema,
    /** Faz parte do pacote inicial de 5 (★, §6.2). */
    recommended: z.boolean(),
    /** Posição na biblioteca (a ordem do §3). */
    order: z.number().int().nonnegative(),
    name: NameL10nSchema,
    defaultSchedule: ChecklistScheduleSchema,
    items: z.array(ChecklistTemplateItemSchema).min(1).max(CHECKLIST_ITEMS_MAX),
  })
  .refine((t) => new Set(t.items.map((i) => i.id)).size === t.items.length, {
    message: 'itemId repetido',
    path: ['items'],
  });
export type ChecklistTemplate = z.infer<typeof ChecklistTemplateSchema>;

/** Faixa de fábrica (§3.9): valor de São Paulo para o mundo todo (decisão 19). */
export const ChecklistFactoryRangeSchema = z
  .object({
    min: z.number().nullable(),
    max: z.number().nullable(),
    unit: ChecklistUnitSchema,
    /** Texto curto da fonte legal, mostrado embaixo da faixa na sheet. */
    source: ChecklistL10nSchema,
  })
  .superRefine(refineRange);
export type ChecklistFactoryRange = z.infer<typeof ChecklistFactoryRangeSchema>;

/**
 * Doc pai da biblioteca — `config/checklistTemplates`: as 13 faixas de fábrica
 * com a fonte, e a versão do catálogo publicada (hash do conteúdo, para o
 * script de sync saber se há o que publicar).
 */
export const ChecklistLibrarySchema = z
  .object({
    catalogVersion: z.string().min(1),
    templateCount: z.number().int().nonnegative(),
    ranges: z.record(ChecklistRangeKeySchema, ChecklistFactoryRangeSchema),
    syncedAtMillis: MillisSchema,
  })
  .superRefine((lib, ctx) => {
    for (const key of ALL_CHECKLIST_RANGE_KEYS) refineBothSidesOnlySanitizer(key, lib.ranges[key], ctx, ['ranges', key]);
  });
export type ChecklistLibrary = z.infer<typeof ChecklistLibrarySchema>;
